import { Component, EventEmitter, Input, OnDestroy, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Filter } from './filters';
import { FilterComponent } from './filter/filter.component';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FiltersFormBuilderService } from './filters-form-builder.service';
import { MatIconModule } from '@angular/material/icon';
import { auditTime, map, Subject, takeUntil, tap } from 'rxjs';
import { FilterGroupComponent } from './filter/filter-group/filter-group.component';

@Component({
  selector: 'cbre-filters',
  imports: [CommonModule, MatIconModule, FilterComponent, ReactiveFormsModule, FilterGroupComponent],
  providers: [FiltersFormBuilderService],
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
})
export class FiltersComponent implements OnDestroy {
  public readonly filters$ = signal<Filter[]>([]);
  @Input() public set filters(filters: Filter[]) {
    this.filters$.set(filters);
    this._destroy$.next();
    this.form = this._formBuilder.buildForm(filters);
    this._setupFormListeners();
  }

  @Input() public aggregations!: Record<string, any>;
  @Input() public canRemoveFilter = false;

  @Output() public removeFilter = new EventEmitter<Filter>();
  @Output() public valueChanges = new EventEmitter<Record<string, any>>();
  @Output() public filtersChange = new EventEmitter<Record<string, any>>();

  public form: FormGroup<Record<string, any>> = new FormGroup({});

  private _destroy$ = new Subject<void>();

  constructor(private _formBuilder: FiltersFormBuilderService) {}

  private _setupFormListeners(): void {
    this.form.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        auditTime(250),
        tap(value => this.valueChanges.emit(value)),
        map(value => this.mapFilterValues(this.filters$(), value))
      )
      .subscribe(filters => this.filtersChange.emit(filters));
  }

  private mapFilterValues = (filters: Filter[], formValues: Record<string, any>): Filter[] => {
    return filters.map(filter => {
      const values =
        filter.type === 'group'
          ? this.mapFilterValues(filter.associations as Filter[], formValues)
          : formValues[filter.key];
      const compoundFilters = filter.compoundFilters?.map(cf => ({
        ...cf,
        values: (cf as any).values != null ? values : null,
      }));
      return { ...filter, values, ...(compoundFilters !== undefined ? { compoundFilters } : {}) };
    });
  };

  public onFilterOptionChange(updatedFilter: Filter): void {
    const updated = this._applyFilterUpdate(this.filters$(), updatedFilter);
    this.filters$.set(updated);
    const formValues = this.form.getRawValue();
    this.valueChanges.emit(formValues);
    this.filtersChange.emit(this.mapFilterValues(updated, formValues));
  }

  private _applyFilterUpdate(filters: Filter[], updatedFilter: Filter): Filter[] {
    const id = updatedFilter.key ?? updatedFilter.field;
    return filters.map(f => {
      if ((f.key ?? f.field) === id) {
        return {
          ...f,
          includeEmpty: updatedFilter.includeEmpty,
          excludeEmpty: updatedFilter.excludeEmpty,
          excludeSelection: updatedFilter.excludeSelection,
          ...(updatedFilter.compoundFilters !== undefined ? { compoundFilters: updatedFilter.compoundFilters } : {}),
        };
      }
      if (f.type === 'group' && f.associations?.length) {
        return { ...f, associations: this._applyFilterUpdate(f.associations, updatedFilter) };
      }
      return f;
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
  }
}
